/**
 * The "load new" pattern for newest-first feeds (Profile activity, Message activity). A
 * background check reads the first page on its own cache key, so the table does not move while
 * the user reads it. The table then offers the newer entries as a "Load N new" row.
 */

/** How often the "load new" check reads the first page: every 15s, four times a minute. */
export const NEW_ITEMS_CHECK_INTERVAL_MS = 15_000;

export interface NewItemsCount {
  /** How many entries are newer than the list. */
  count: number;
  /** True when the API has more new entries past the checked page. */
  hasMore: boolean;
}

/**
 * How many entries at the top of the latest page the list does not show yet. The feed is newest
 * first, so these are the entries newer than the list. When none of the page is loaded, all of it
 * is new, and `latestHasMore` (the page has a next cursor) says whether more new entries exist.
 */
export function countNewItems(
  latestIds: readonly string[],
  loadedIds: ReadonlySet<string>,
  latestHasMore: boolean,
): NewItemsCount {
  const firstLoadedIndex = latestIds.findIndex((id) => loadedIds.has(id));
  if (firstLoadedIndex !== -1) {
    return { count: firstLoadedIndex, hasMore: false };
  }
  return { count: latestIds.length, hasMore: latestHasMore };
}

/** "Load 3 new", or "Load 50+ new" when more new entries exist past the checked page. */
export function loadNewLabel({ count, hasMore }: NewItemsCount): string {
  return hasMore ? `Load ${count}+ new` : `Load ${count} new`;
}
